import { useCallback, useState } from "react";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { FadeUp } from "./Shared";
import TurnstileWidget from "@/components/TurnstileWidget";

const inputCls =
  "w-full bg-white/5 border border-white/15 px-4 py-3 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-[#E5FF00] transition-colors duration-200";

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [captcha, setCaptcha] = useState("");
  const [sending, setSending] = useState(false);
  const onToken = useCallback((token) => setCaptcha(token), []);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!captcha) {
      toast.error("Please complete the captcha check first.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", { ...form, turnstile_token: captcha });
      toast.success("Message sent. We'll get back to you shortly.");
      setForm({ name: "", email: "", company: "", message: "" });
      setCaptcha("");
      if (window.turnstile) window.turnstile.reset();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-28 lg:py-36 bg-[#080808] border-t border-white/10" data-testid="section-contact">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-16">
        <FadeUp className="lg:col-span-5">
          <p className="font-mono-tech text-[11px] tracking-[0.3em] text-[#E5FF00] mb-6">/CONTACT</p>
          <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95]">
            TALK TO A HUMAN.
          </h2>
          <p className="mt-6 text-zinc-400 leading-relaxed max-w-md">
            Rolling out sentiment intelligence across a mall network, a festival season or an entire campus? Tell us about
            your venue and we'll shape the deployment with you.
          </p>
          <p className="mt-8 font-mono-tech text-[11px] tracking-[0.2em] text-zinc-500">
            OR WRITE TO <span className="text-white">CONTACT-PULSE@LYPINN.COM</span>
          </p>
        </FadeUp>
        <FadeUp delay={0.15} className="lg:col-span-7">
          <form onSubmit={submit} className="border border-white/15 bg-[#0a0a0a] p-8 lg:p-10 space-y-4" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <input required minLength={1} maxLength={100} placeholder="Your name" value={form.name} onChange={set("name")} className={inputCls} data-testid="contact-name-input" />
              <input required type="email" placeholder="Work email" value={form.email} onChange={set("email")} className={inputCls} data-testid="contact-email-input" />
            </div>
            <input maxLength={120} placeholder="Business / venue (optional)" value={form.company} onChange={set("company")} className={inputCls} data-testid="contact-company-input" />
            <textarea required minLength={5} maxLength={5000} rows={5} placeholder="Tell us about your venue, footfall and what you want to learn from your audience..." value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} data-testid="contact-message-input" />
            <TurnstileWidget onToken={onToken} testid="contact-turnstile" />
            <button
              type="submit"
              disabled={sending}
              className="group flex items-center gap-2 bg-[#E5FF00] text-black font-display font-bold text-sm tracking-wide px-8 py-4 hover:bg-white transition-colors duration-200 disabled:opacity-50"
              data-testid="contact-submit-btn"
            >
              {sending ? "SENDING..." : "SEND MESSAGE"}
              <Send size={15} className="transition-transform duration-200 group-hover:translate-x-0.5" />
            </button>
          </form>
        </FadeUp>
      </div>
    </section>
  );
}
